<template>
  <ul class="flex gap-5">
    <li
      v-for="(card, index) in cards"
      :key="card.outerTitle"
      class="h-[400px] overflow-hidden rounded border-solid border-blue-400 bg-blue-400/10 shadow-normal shadow-gray-400 transition-all duration-300"
      :class="[
        provideWidthClass(index),
        current === index ? 'border-t-4' : 'border-t-0',
      ]"
      @mouseenter="$emit('mouseenter', index)"
    >
      <div
        class="relative flex h-full w-full flex-col bg-cover bg-center bg-no-repeat p-5"
        :class="
          current !== index &&
          'before:absolute before:left-0 before:top-0 before:h-full before:w-full before:bg-gradient-to-b before:from-black/60 before:from-10% before:to-transparent before:content-[\'\']'
        "
        :style="provideCardStyle(index, card.bgImg)"
      >
        <h3
          class="relative break-words pb-2 text-lg font-bold"
          :class="current === index ? 'text-black' : 'text-white'"
        >
          {{ current === index ? card.innerTitle : card.outerTitle }}
        </h3>
        <div
          v-show="current === index"
          class="container-scrollbar flex-1 overflow-y-auto overflow-x-hidden"
        >
          <p v-if="isEU && card.description" class="mb-3 text-sm">
            {{ card.description }}
          </p>

          <ul v-if="card.traits.length" class="mb-3 list-disc pl-6">
            <li v-for="trait in card.traits" :key="trait" class="text-sm">
              {{ trait }}
            </li>
          </ul>

          <template v-if="card.solutionApps.length">
            <h4 class="mb-2 text-base font-bold">
              {{ card.productSectionTitle }}
            </h4>
            <ul class="flex max-w-[330px] flex-wrap items-center">
              <li
                v-for="app in card.solutionApps"
                :key="app.productName"
                class="mb-2 w-1/3"
              >
                <TheNuxtLink
                  v-if="app.productUrl"
                  :to="app.productUrl"
                  :title="app.productName"
                  class="block hover:text-blue-500 hover:underline"
                >
                  <figure
                    class="mx-auto mb-1 h-10 w-10 overflow-hidden rounded-lg"
                  >
                    <img
                      :src="provideImgUrl(app.productIcon)"
                      alt=""
                      loading="lazy"
                      class="pointer-events-none h-full w-full object-cover"
                    />
                  </figure>
                  <span class="block text-center text-xs font-bold">
                    {{ app.productName }}
                  </span>
                </TheNuxtLink>
              </li>
            </ul>
          </template>

          <template v-if="card.successStories?.length && isCN">
            <h4 class="mb-2 mt-1 text-base font-bold">
              {{ card.successStoryTitle }}
            </h4>
            <ul class="grid max-w-[330px] grid-cols-3 gap-2">
              <li
                v-for="successItem in card.successStories"
                :key="successItem.name"
              >
                <TheNuxtLink
                  :to="successItem.url"
                  :title="successItem.name"
                  class="block overflow-hidden rounded bg-white hover:shadow-normal hover:shadow-gray-400"
                >
                  <figure
                    v-if="successItem.icon"
                    class="relative aspect-video w-full overflow-hidden"
                  >
                    <img
                      :src="provideImgUrl(successItem.icon)"
                      :alt="successItem.name"
                      loading="lazy"
                      class="pointer-events-none absolute left-0 top-0 h-full w-full object-contain"
                    />
                  </figure>
                </TheNuxtLink>
              </li>
            </ul>
          </template>
        </div>
        <div
          v-show="current === index"
          class="flex w-full items-center justify-between pt-2"
        >
          <TheButton
            v-if="card.btnOneUrl"
            :title="card.btnOne"
            :to="card.btnOneUrl"
            class="mr-5 w-full"
          >
            <span class="text-sm">{{ card.btnOne }}</span>
          </TheButton>
          <TheButton
            :id="contextAcnTrackingID"
            :title="card.btnTwo"
            :to="contextDomainSupportURL"
            :type="card.btnOneUrl ? 'outline' : 'primary'"
            class="w-full"
            :class="card.btnOneUrl && 'bg-white/50'"
          >
            <span class="text-sm">{{ card.btnTwo }}</span>
          </TheButton>
        </div>
      </div>
    </li>
  </ul>
</template>

<script setup lang="ts">
  import type { ISolutionCard } from '@/assets/types/Home';

  interface IProps {
    cards: ISolutionCard[];
    current: number;
  }

  const props = withDefaults(defineProps<IProps>(), {
    cards: () => [],
  });

  defineEmits<{
    (e: 'mouseenter', index: number): void;
  }>();

  const { locale } = useI18n();
  const { provideImgUrl, provideBackgroundUrl } = useHomeData();
  const { isCN, isEU } = useLanguage();

  const contextDomainSupportURL = computed((): string => {
    return `/${locale.value}/marketplace/help/sales-support?IssueType=1`;
  });

  const contextAcnTrackingID = computed((): string | undefined => {
    if (isCN.value) {
      return 'tracking_ssupport';
    }
    return undefined;
  });

  const provideWidthClass = (index: number): string => {
    if (props.cards.length === 5) {
      return props.current === index ? 'w-[32%]' : 'w-[16%]';
    }
    if (props.cards.length === 4) {
      return props.current === index ? 'w-[32%]' : 'w-[21.33%]';
    }
    if (props.cards.length === 3) {
      return 'w-[32%]';
    }
    return 'w-full';
  };

  const provideCardStyle = (index: number, bgImg: string): string => {
    if (props.current !== index) {
      return provideBackgroundUrl(bgImg);
    }
    return '';
  };
</script>
